<script setup lang="ts">
import type { Suggestion } from 'types/models/product'
import { useDebounceFn } from '@vueuse/core'
import { productUrl } from 'lib/routing'
import { useSpeechBubbleStore } from '~/stores/speechBubble'

interface Props {
  expanded: boolean
}

defineProps<Props>()

const emit = defineEmits(['searchExpand', 'searchCollapse', 'closeMenu'])
const SEARCH_INPUT_MINIMUM = 3
const { $t } = useNuxtApp()

const searchStore = useSearchStore()
const router = useRouter()
const gtm = useGTM()
const { $currentStorefront } = useNuxtApp()
const speechBubbleStore = useSpeechBubbleStore()

const suggestions = ref<Suggestion[] | []>([])
const searchResults = ref<number>(0)

const searchInput = ref('')
const loading = ref(false)

function onOpenSearch(open: boolean) {
  if (open) {
    emit('searchExpand')
  }
  else {
    emit('searchCollapse')
  }
}

const onInput = useDebounceFn(async (input: string) => {
  searchInput.value = input
  loading.value = true

  if (input.length < SEARCH_INPUT_MINIMUM) {
    suggestions.value = []
  }
  else {
    try {
      const data = await searchStore.fetchSuggestions(input)
      suggestions.value = data.products ?? []
      searchResults.value = data.count
    }
    catch (error: any) {
      if (error && error.status) // http error only
        console.error(error)
    }
    finally {
      loading.value = false
      gtm.pushSearchEvent({ results: searchResults.value, query: searchInput.value })
    }
  }
}, 500)

async function onSelect(suggestion: Suggestion | undefined) {
  try {
    if (suggestion) {
      gtm.pushSearchSuggestionClick({ results: searchResults.value, query: searchInput.value, sku: suggestion.id })
      await router.push(productUrl(suggestion.id))
    }
    else {
      gtm.pushShowSearchResultsClick({ results: searchResults.value, query: searchInput.value })
      await router.push(
        `/${$currentStorefront.storefrontCode}/search?term=${encodeURI(searchInput.value)}`,
      )
    }
  }
  catch (error) {
    console.error(error)
  }
  finally {
    emit('searchCollapse')
    emit('closeMenu')
  }
}
</script>

<template>
  <div class="search-container">
    <ComboboxRoot
      :open-on-focus="true"
      :open-on-click="true"
      :open="expanded"
      class="ComboboxRoot"
      :ignore-filter="true"
      @update:open="onOpenSearch"
    >
      <ComboboxAnchor
        class="ComboboxAnchor"
        :class="[{
          expanded,
        }]"
      >
        <ComboboxInput
          class="ComboboxInput"
          :placeholder="$t('search')"
          @update:model-value="onInput"
        />
      </ComboboxAnchor>
      <ComboboxContent
        class="ComboboxContent"
      >
        <ComboboxViewport class="ComboboxViewport">
          <ComboboxEmpty
            v-if="searchInput.length >= SEARCH_INPUT_MINIMUM && !loading && suggestions.length === 0"
            class="ComboboxEmpty"
          >
            {{ $t('searchNoResultsTitle', { term: searchInput }) }}
          </ComboboxEmpty>
          <ComboboxGroup v-if="suggestions.length">
            <ComboboxItem
              v-if="suggestions.length > 0"
              data-test="search-suggestion"
              class="ComboboxItem showMore"
              :value="null"
              @select="onSelect(undefined)"
            >
              <span>
                {{ $t('showMore') }}
              </span>
            </ComboboxItem>
            <ComboboxItem
              v-for="suggestion in suggestions"
              :key="suggestion.id"
              data-test="search-suggestion"
              class="ComboboxItem"
              :value="suggestion"
              @select="() => onSelect(suggestion)"
            >
              <ImageResponsive
                provider="fastly"
                :src="suggestion?.product?.photo ?? ''"
                alt=""
                :max-width="40"
                ratio="1:1"
              />
              <span>
                {{ suggestion.product.title }}
              </span>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
    <HeaderSpeechBubble
      v-show="speechBubbleStore.position === 'search'"
      :position="speechBubbleStore.position"
    />
  </div>
</template>

<style lang="scss" scoped>
.search-container {
  position: relative;
}

.expanded {
  background-color: white;
  border: 2px solid;
}

.showMore {
  display: flex;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray);
  opacity: 0.9;
}

.showMore[data-highlighted] {
  background-color: var(--green);
  opacity: 1;
}
</style>
